import { HostListener, Directive } from '@angular/core';
import { PlotlyService } from 'angular-plotly.js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Plotly } from 'angular-plotly.js/lib/plotly.interface';

export enum KEY_CODE {
  A = 65,
  Y = 89,
  X = 88,
  ADD = 107,
  SUBSTRACT = 109,
}

export abstract class Plot {
  protected xaxis = 'Time in s';

  protected abstract createLayoutTwoAxis(
    materialName: string,
    unitY1: string,
    unitY2: string,
    rangeMin: number,
    rangeMax: number,
    type: string
  ): any;
  protected abstract createLayout(
    materialName: string,
    xtitle: string,
    ytitle: string,
    rangeMin: number,
    rangeMax: number
  ): any;
  protected abstract createLogLayout(
    materialName: string,
    xtitle: string,
    ytitle: string,
    rangeMin: number,
    rangeMax: number,
    type: string
  ): any;
  protected abstract createLine(
    x: number[],
    y: number[],
    name: string,
    group: string,
    legend: boolean,
    meta: {},
    isHidden: boolean
  ): any;
}

@Directive()
export class PlotLayout extends Plot {
  private mouseEventX: number = 0;

  public densityViscoChart: any[] = [];
  public densityViscoLayout: any = {};
  public materialName: string = '';
  public isHiddenCurves = false;

  constructor(public plotlyService: PlotlyService, public snackBar: MatSnackBar) {
    super();
  }

  protected createLayoutTwoAxis(
    materialName: string,
    unitY1: string,
    unitY2: string,
    rangeMin?: number,
    rangeMax?: number,
    type?: string
  ) {
    const layout = {
      height: 500,
      title: materialName,
      showlegend: true,
      hovermode: 'closest',
      hoverdistance: 25,
      legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
      },
      yaxis2: {
        title: unitY2,
        overlaying: 'y',
        side: 'right',
        type: type,
        autorange: true,
      },
      yaxis: {
        title: unitY1,
        side: 'left',
      },
      xaxis: {
        title: this.xaxis,
        // range: [rangeMin, rangeMax],
      },
    };
    return layout;
  }

  protected createLayout(materialName: string, xtitle: string, ytitle: string, rangeMin?: number, rangeMax?: number) {
    const layout = {
      height: 500,
      title: materialName,
      hovermode: 'closest',
      hoverdistance: 25,
      yaxis: { title: ytitle },
      xaxis: {
        title: xtitle,
        // range: [rangeMin, rangeMax],
      },
    };
    return layout;
  }

  protected createLogLayout(
    materialName: string,
    xtitle: string,
    ytitle: string,
    rangeMin: number,
    rangeMax: number,
    type: string
  ) {
    const layout = {
      height: 500,
      title: materialName,
      yaxis: {
        title: ytitle,
        type: type,
        autorange: true,
      },
      xaxis: {
        title: xtitle,
        // range: [rangeMin, rangeMax],
      },
    };
    return layout;
  }

  protected createLine(
    x: number[],
    y: number[],
    name?: string,
    group?: string,
    legend?: boolean,
    meta?: {},
    isHidden?: boolean
  ): any {
    let line: any;
    line = {
      x: [],
      y: [],
      mode: 'lines',
      name: name,
      showlegend: legend,
      legendgroup: group,
      meta: meta,
      visible: isHidden,
      hovertext: name,
    };
    line.x = x;
    line.y = y;
    return line;
  }

  protected createLineDotted(x: number[], y: number[], name: string): any {
    let line: any;
    line = { x: [], y: [], mode: 'lines', line: { dash: 'dot' }, showlegend: true, name: name };
    line.x = x;
    line.y = y;
    return line;
  }

  protected createLineDottedRight(x: number[], y: number[], name: string): any {
    let line: any;
    line = {
      x: [],
      y: [],
      mode: 'lines',
      yaxis: 'y2',
      line: { dash: 'dot' },
      showlegend: true,
      name: name,
    };
    line.x = x;
    line.y = y;
    return line;
  }

  protected createPoint(x: number[], y: number[], name: string): any {
    let point: any;
    point = { x: [], y: [], mode: 'markers', type: 'scatter', showlegend: true, name: name };
    point.x = [x];
    point.y = [y];
    return point;
  }

  public onInitialized() {
    this.densityViscoLayout = this.createLayoutTwoAxis(this.materialName, 'Density g/L', 'Eta in Pas', 0, 0, 'log');

    setTimeout(() => {
      const div = this.plotlyService.getInstanceByDivId('plotDiv');
      if (div) {
        div.addEventListener('mousemove', (event) => {
          this.mouseEventX = event.x;
        });

        div.addEventListener('mousedown', (e) => {
          this.getClickedPoint();
        });
      }
    }, 500);
  }

  public onInitDensityLayout() {
    this.densityViscoLayout = this.createLayout(this.materialName, 'Time in s', 'Density g/L', 0, 0);
  }

  public onInitViscoLayout() {
    this.densityViscoLayout = this.createLogLayout(this.materialName, 'Time in s', 'Eta in pas', 0, 0, 'log');
  }

  public getClickedPoint() {
    const div = this.plotlyService.getInstanceByDivId('plotDiv') as Plotly.Layout;

    const rangeMinX = div['layout']['xaxis']['range'][0];
    const rangeMaxX = div['layout']['xaxis']['range'][1];

    const xPoint = this.mouseEventX - div['_fullLayout']['margin']['l'] - div.getBoundingClientRect().left;
    const constRangeX = rangeMaxX - rangeMinX;

    const pixelRangeWidth = div['_fullLayout']['_size']['w'];

    const xCoor = (xPoint / pixelRangeWidth) * constRangeX + rangeMinX;

    var intValue = parseInt(xCoor, 10);
  }

  @HostListener('window:keyup', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    if (event.keyCode === KEY_CODE.X) {
      this.getClickedPoint();
    }
  }

  @HostListener('window:mousedown', ['$event'])
  onMousedown() {
    const div = this.plotlyService.getInstanceByDivId('plotDiv');
    if (div) {
      div.addEventListener('mousedown', (e) => {
        this.getClickedPoint();
      });
    }
  }

  // foaming

  protected addCurves(data: any[]) {
    this.densityViscoChart = data;
    return this.densityViscoChart;
  }

  private deleteLinyByName(name: string) {
    for (let i = 0; i < this.densityViscoChart.length; i++) {
      if (this.densityViscoChart[i]['name'] === name) {
        this.densityViscoChart.splice(i, 1);
      }
    }
  }

  public addTargetDensityLine(yPoint: number) {
    const xAxisRange = this.densityViscoLayout['xaxis']['range'][1];

    this.deleteLinyByName('Target Density');
    const yPointArr: number[] = [];
    const xPoint: number[] = [];
    for (let i = 0; i <= xAxisRange; i++) {
      yPointArr.push(yPoint);
      xPoint.push(i);
    }
    this.densityViscoChart.push(this.createLineDotted(xPoint, yPointArr, 'Target Density'));
  }

  public addSimulationLine(xPoint: number) {
    const yAxisRange = this.densityViscoLayout['yaxis']['range'][1];
    this.deleteLinyByName('Simulation Time');
    const xPointArr: number[] = [];
    const yPoint: number[] = [];

    const step = yAxisRange / 5;

    for (let i = 0; i <= yAxisRange; i += step) {
      yPoint.push(i);
      xPointArr.push(xPoint);
    }
    this.densityViscoChart.push(this.createLineDotted(xPointArr, yPoint, 'Simulation Time'));
  }

  public addInjectionTimeLine(xPoint: number) {
    const yAxisRange = this.densityViscoLayout['yaxis']['range'][1];
    this.deleteLinyByName('Injection Time');
    const xPointArr: number[] = [];
    const yPoint: number[] = [];

    const step = yAxisRange / 5;

    for (let i = 0; i <= yAxisRange; i += step) {
      yPoint.push(i);
      xPointArr.push(xPoint);
    }
    this.densityViscoChart.push(this.createLineDotted(xPointArr, yPoint, 'Injection Time'));
  }

  public addViscoCreamTime(xPoint: number) {
    // const yAxisRange = this.viscoRotLayout['yaxis']['range'][1];
    const yAxisRange = this.densityViscoChart[1]['y'].slice(-1)[0];
    this.deleteLinyByName('Cream time');

    const xPointArr: number[] = [];
    const yPoint: number[] = [];

    const step = yAxisRange / 100;

    for (let i = 0; i <= yAxisRange; i += step) {
      yPoint.push(i);
      xPointArr.push(xPoint);
    }
    this.densityViscoChart.push(this.createLineDottedRight(xPointArr, yPoint, 'Cream time'));
  }

  public addViscoSolidTime(xPoint: number) {
    const yAxisRange = this.densityViscoChart[1]['y'].slice(-1)[0];
    this.deleteLinyByName('Solid time');

    const xPointArr: number[] = [];
    const yPoint: number[] = [];

    const step = yAxisRange / 100;

    for (let i = 0; i <= yAxisRange; i += step) {
      yPoint.push(i);
      xPointArr.push(xPoint);
    }
    this.densityViscoChart.push(this.createLineDottedRight(xPointArr, yPoint, 'Solid time'));
  }
}
